import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useGetClientUserQuery, useResetPasswordMutation } from '../../api/client-users-api';
import { clientUserPasswordResetModalId, clientUsersAlertId } from '../../constants/constants';
import { selectActiveClientUserId, setActiveClientUserId } from '../../store/client-users-slice';
import { Spin } from 'antd';

const ClientUserPasswordResetModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-users', 'common']);
  const dispatch = useDispatch();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();
  const activeClientUserId = useAppSelector(selectActiveClientUserId);
  const [resetPassword, { isLoading: isLoadingResetPassword }] = useResetPasswordMutation();

  const { data: clientUserData, isFetching: isClientUserDataFetching } = useGetClientUserQuery(
    activeClientUserId || '',
    {
      skip: !activeClientUserId,
    },
  );

  const closeClientUserPasswordResetModal = () => {
    dispatch(closeModal(clientUserPasswordResetModalId));
    dispatch(setActiveClientUserId(null));
  };

  const confirmReset = () => {
    if (!activeClientUserId) {
      return;
    }
    resetConfirmationError();
    if (!clientUserData?.email) {
      closeClientUserPasswordResetModal();
      return;
    }

    resetPassword(activeClientUserId)
      .unwrap()
      .then(() => {
        dispatch(
          setAlert({
            id: clientUsersAlertId,
            type: 'success',
            title: t('clientUserPasswordResetModal.message.successPasswordReset'),
          }),
        );
        closeClientUserPasswordResetModal();
      })
      .catch(() => addConfirmationError(t('clientUserPasswordResetModal.confirmationPasswordReset')));
  };

  return (
    <Modal
      title={isClientUserDataFetching ? '' : t('clientUserPasswordResetModal.passwordReset')}
      onCancel={closeClientUserPasswordResetModal}
      isLoading={isLoadingResetPassword}
      {...(!isClientUserDataFetching && {
        customSubmitText: t('action.passwordReset'),
      })}
      submitButtonProps={{ disabled: !clientUserData?.email }}
      customDeleteText={t('action.cancel', { ns: 'common' })}
      onOk={confirmReset}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Spin spinning={isClientUserDataFetching}>
        {clientUserData &&
          (!clientUserData?.email ? (
            <Trans
              i18nKey="clientUserPasswordResetModal.message.missingEmail"
              ns="client-users"
              t={t}
              values={{ name: clientUserData?.name || '' }}
            />
          ) : (
            <ModalConfirmationContent
              confirmDeleteText={
                <Trans
                  i18nKey={'clientUserPasswordResetModal.confirmationPasswordReset'}
                  t={t}
                  ns="client-users"
                  values={{ name: clientUserData?.name || '' }}
                />
              }
              confirmationError={confirmationError}
            />
          ))}
      </Spin>
    </Modal>
  );
};

const ClientUserPasswordResetModal = () => {
  return <LazyModal modalId={clientUserPasswordResetModalId} Component={ClientUserPasswordResetModalContent} />;
};

export default ClientUserPasswordResetModal;
